import {
	Action,
	BillingCycle,
	CURRENCY_LABELS,
	Currency,
	type CustomerPlan,
	type UserAccessProfile,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	AlertCircle,
	CheckCircle2,
	CreditCard,
	HelpCircle,
	Plus,
	Star,
	X,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAccessProfileQueryOptions } from "@/api/accessProfile";
import {
	fetchOrganizationCustomerPlansQueryOptions,
	useCreateCustomerPlan,
	useUpdateCustomerPlan,
} from "@/api/customerPlans";
import { fetchOrganizationEstablishmentsQueryOptions } from "@/api/establishments";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { can } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import {
	calculateMonthlyAverage,
	formatCurrency,
	getOrganizationCurrencySymbol,
} from "@/utils/planPricingUtils";
import { CustomerPlanList } from "./-components/CustomerPlanList";

// Constants for form validation
const MAX_NAME_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;

const STANDARD_SERVICE_KEYS = [
	"pilates",
	"yoga",
	"meditation",
	"functionalTraining",
	"stretching",
	"physiotherapy",
] as const;

type StandardServiceKey = (typeof STANDARD_SERVICE_KEYS)[number];

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customer-plans/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Preload customer plans data and access profile
		context.queryClient.ensureQueryData(
			fetchOrganizationCustomerPlansQueryOptions(userId, token, orgId),
		);

		context.queryClient.ensureQueryData(
			fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
		);

		const profile = await context.queryClient.ensureQueryData(
			fetchAccessProfileQueryOptions(userId, token, orgId),
		);

		// Guard: must have VIEW_CUSTOMER_PLANS in this org
		const canView = can(
			profile as UserAccessProfile,
			Action.VIEW_CUSTOMER_PLANS,
		);
		if (!canView) {
			throw new Error("Access denied to customer plans page");
		}

		return { userId, token, orgId, profile };
	},
	component: CustomerPlansPage,
});

function CustomerPlansPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const { userId, token, profile } = Route.useLoaderData() as {
		userId: string;
		token: string;
		orgId: string;
		profile: UserAccessProfile;
	};

	const canManage = can(profile, Action.MANAGE_CUSTOMER_PLANS);
	const currentOrganization = useCurrentOrganization();

	// State for dialogs and forms
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [editingPlan, setEditingPlan] = useState<CustomerPlan | null>(null);
	const [validationErrors, setValidationErrors] = useState<{
		name?: string;
		description?: string;
		price?: string;
		features?: string;
		weeklySessionsLimit?: string;
	}>({});

	// Form state
	const [formData, setFormData] = useState({
		name: "",
		description: "",
		price: "",
		currency: currentOrganization.defaultCurrency,
		billingCycle: BillingCycle.MONTHLY,
		features: [{ id: crypto.randomUUID(), value: "" }],
		establishmentIds: [] as string[],
		weeklySessionsLimit: "",
		isActive: true,
	});

	// Get customer plans data
	const { data: customerPlans } = useSuspenseQuery(
		fetchOrganizationCustomerPlansQueryOptions(userId, token, orgId),
	);

	// Get establishments data
	const { data: establishments } = useSuspenseQuery(
		fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
	);

	// Mutations
	const createCustomerPlanMutation = useCreateCustomerPlan();
	const updateCustomerPlanMutation = useUpdateCustomerPlan();

	const resetForm = () => {
		setFormData({
			name: "",
			description: "",
			price: "",
			currency: currentOrganization.defaultCurrency,
			billingCycle: BillingCycle.MONTHLY,
			features: [{ id: crypto.randomUUID(), value: "" }],
			establishmentIds: [],
			weeklySessionsLimit: "",
			isActive: true,
		});
		setValidationErrors({});
	};

	const addFeature = () => {
		setFormData({
			...formData,
			features: [...formData.features, { id: crypto.randomUUID(), value: "" }],
		});
		if (validationErrors.features) {
			setValidationErrors({ ...validationErrors, features: undefined });
		}
	};

	const addFeatureWithValue = (value: string) => {
		const trimmedValue = value.trim();
		if (!trimmedValue) return;

		// Avoid adding duplicated services (case-insensitive)
		const alreadyExists = formData.features.some(
			(feature) =>
				feature.value.trim().toLowerCase() === trimmedValue.toLowerCase(),
		);
		if (alreadyExists) {
			if (validationErrors.features) {
				setValidationErrors({ ...validationErrors, features: undefined });
			}
			return;
		}

		const emptyIndex = formData.features.findIndex(
			(feature) => !feature.value.trim(),
		);
		const newFeatures = [...formData.features];

		if (emptyIndex !== -1) {
			newFeatures[emptyIndex] = {
				...newFeatures[emptyIndex],
				value: trimmedValue,
			};
		} else {
			newFeatures.push({ id: crypto.randomUUID(), value: trimmedValue });
		}

		setFormData({
			...formData,
			features: newFeatures,
		});
		if (validationErrors.features) {
			setValidationErrors({ ...validationErrors, features: undefined });
		}
	};

	const removeFeature = (index: number) => {
		const newFeatures = formData.features.filter((_, i) => i !== index);
		setFormData({
			...formData,
			features:
				newFeatures.length > 0
					? newFeatures
					: [{ id: crypto.randomUUID(), value: "" }],
		});
	};

	const updateFeature = (index: number, value: string) => {
		const newFeatures = [...formData.features];
		newFeatures[index] = { ...newFeatures[index], value };
		setFormData({
			...formData,
			features: newFeatures,
		});
		if (validationErrors.features && value.trim()) {
			setValidationErrors({ ...validationErrors, features: undefined });
		}
	};

	const validateForm = () => {
		const errors: typeof validationErrors = {};

		if (!formData.name.trim()) {
			errors.name = t("customerPlans.management.form.validation.nameRequired");
		}

		if (!formData.description.trim()) {
			errors.description = t(
				"customerPlans.management.form.validation.descriptionRequired",
			);
		}

		const price = Number.parseFloat(formData.price);
		if (Number.isNaN(price) || price <= 0) {
			errors.price = t(
				"customerPlans.management.form.validation.priceRequired",
			);
		}

		const validFeatures = formData.features.filter(
			(f) => f.value.trim().length > 0,
		);
		if (validFeatures.length === 0) {
			errors.features = t(
				"customerPlans.management.form.validation.featuresRequired",
			);
		}

		if (formData.weeklySessionsLimit.trim()) {
			const weeklyLimit = Number.parseInt(formData.weeklySessionsLimit, 10);
			if (Number.isNaN(weeklyLimit) || weeklyLimit < 1 || weeklyLimit > 20) {
				errors.weeklySessionsLimit = t(
					"customerPlans.management.form.validation.weeklySessionsLimitInvalid",
				);
			}
		}

		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleCreateCustomerPlan = async () => {
		if (!validateForm()) return;

		try {
			const validFeatures = formData.features
				.filter((f) => f.value.trim().length > 0)
				.map((f) => f.value.trim());

			await createCustomerPlanMutation.mutateAsync({
				token,
				organizationId: orgId,
				data: {
					name: formData.name.trim(),
					description: formData.description.trim(),
					price: formData.price.trim(), // Send as string to maintain precision
					currency: formData.currency,
					billingCycle: formData.billingCycle,
					features: validFeatures,
					establishmentIds: formData.establishmentIds,
					weeklySessionsLimit: formData.weeklySessionsLimit.trim()
						? Number.parseInt(formData.weeklySessionsLimit, 10)
						: undefined,
					isActive: formData.isActive,
				},
			});

			// Success toast is handled by the mutation hook
			setIsCreateDialogOpen(false);
			resetForm();
		} catch (error) {
			// Error toast is handled by the mutation hook
			console.error("Failed to create customer plan:", error);
		}
	};

	const handleEditCustomerPlan = (plan: CustomerPlan) => {
		setEditingPlan(plan);
		setFormData({
			name: plan.name,
			description: plan.description,
			price: plan.price.toString(),
			currency: plan.currency as Currency,
			billingCycle: plan.billingCycle,
			features:
				plan.features.length > 0
					? plan.features.map((feature) => ({
							id: crypto.randomUUID(),
							value: feature,
						}))
					: [{ id: crypto.randomUUID(), value: "" }],
			establishmentIds: plan.establishmentIds || [],
			weeklySessionsLimit: plan.weeklySessionsLimit?.toString() || "",
			isActive: plan.isActive,
		});
	};

	const handleUpdateCustomerPlan = async () => {
		if (!editingPlan || !validateForm()) return;

		try {
			const validFeatures = formData.features
				.filter((f) => f.value.trim().length > 0)
				.map((f) => f.value.trim());

			await updateCustomerPlanMutation.mutateAsync({
				token,
				organizationId: orgId,
				customerPlanId: editingPlan.id,
				data: {
					name: formData.name.trim(),
					description: formData.description.trim(),
					price: formData.price.trim(), // Send as string to maintain precision
					currency: formData.currency,
					billingCycle: formData.billingCycle,
					features: validFeatures,
					establishmentIds: formData.establishmentIds,
					weeklySessionsLimit: formData.weeklySessionsLimit.trim()
						? Number.parseInt(formData.weeklySessionsLimit, 10)
						: undefined,
					isActive: formData.isActive,
				},
			});

			// Success toast is handled by the mutation hook
			setEditingPlan(null);
			resetForm();
		} catch (error) {
			// Error toast is handled by the mutation hook
			console.error("Failed to update customer plan:", error);
		}
	};

	const isLoading =
		createCustomerPlanMutation.isPending ||
		updateCustomerPlanMutation.isPending;

	const quickServiceOptions = STANDARD_SERVICE_KEYS.map(
		(key: StandardServiceKey) => ({
			key,
			label: t(`customerPlans.management.form.quickServices.${key}`),
		}),
	);

	return (
		<div className="h-full flex flex-col space-y-4 sm:space-y-6">
			{/* Header */}
			<div
				className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4"
				data-testid="customer-plans-header"
			>
				<PageHeader
					title={t("customerPlans.management.title")}
					description={t("customerPlans.management.description")}
					showBackButton={false}
				/>
				{canManage && (
					<Dialog
						open={isCreateDialogOpen}
						onOpenChange={setIsCreateDialogOpen}
					>
						<DialogTrigger asChild>
							<Button
								className="btn btn-primary text-primary-content w-full sm:w-auto"
								data-testid="create-customer-plan-button"
							>
								<Plus className="h-4 w-4 mr-2" />
								{t("customerPlans.management.createNew")}
							</Button>
						</DialogTrigger>
						<DialogContent
							className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto"
							data-testid="create-customer-plan-dialog"
						>
							<DialogHeader className="space-y-3 pb-6 border-b border-base-300">
								<DialogTitle className="text-xl font-semibold text-base-content flex items-center gap-2">
									<CreditCard className="h-5 w-5 text-primary" />
									{t("customerPlans.management.createNew")}
								</DialogTitle>
								<DialogDescription className="text-base-content/70">
									{t("customerPlans.management.description")}
								</DialogDescription>
								<p className="text-xs text-base-content/50 flex items-center gap-1">
									<AlertCircle className="h-3 w-3" />
									{t("customerPlans.management.form.requiredFields")}
								</p>
							</DialogHeader>
							<div className="space-y-6 py-6" data-testid="customer-plan-form">
								{/* Basic Information Section */}
								<div className="space-y-4">
									<div className="flex items-center justify-between pb-2 border-b border-base-200">
										<div className="flex items-center gap-2">
											<div className="w-2 h-2 bg-primary rounded-full" />
											<h3 className="text-lg font-medium text-base-content">
												{t("customerPlans.management.form.sections.basicInfo")}
											</h3>
										</div>
										{formData.name.trim() && formData.description.trim() && (
											<CheckCircle2 className="h-4 w-4 text-success" />
										)}
									</div>
									<div className="grid gap-4">
										<div className="space-y-2">
											<div className="flex items-center justify-between">
												<Label
													htmlFor="name"
													className="text-sm font-medium text-base-content flex items-center gap-1"
												>
													{t("customerPlans.management.form.name")}
													<span className="text-error">*</span>
												</Label>
												<span
													className={`text-xs ${formData.name.length > MAX_NAME_LENGTH ? "text-error" : "text-base-content/50"}`}
												>
													{t("customerPlans.management.form.nameMaxLength", {
														current: formData.name.length,
														max: MAX_NAME_LENGTH,
													})}
												</span>
											</div>
											<Input
												id="name"
												value={formData.name}
												onChange={(e) => {
													const value = e.target.value.slice(
														0,
														MAX_NAME_LENGTH,
													);
													setFormData({ ...formData, name: value });
													if (validationErrors.name) {
														setValidationErrors({
															...validationErrors,
															name: undefined,
														});
													}
												}}
												placeholder={t(
													"customerPlans.management.form.namePlaceholder",
												)}
												className={`h-10 transition-all ${
													validationErrors.name
														? "border-error focus:ring-error/20"
														: formData.name.trim()
															? "border-success/50 focus:border-success focus:ring-success/20"
															: ""
												}`}
												data-testid="customer-plan-name-input"
											/>
											{validationErrors.name ? (
												<p className="text-sm text-error flex items-center gap-1">
													<AlertCircle className="h-3 w-3" />
													{validationErrors.name}
												</p>
											) : (
												<p className="text-xs text-base-content/60">
													{t("customerPlans.management.form.nameHint")}
												</p>
											)}
										</div>
										<div className="space-y-2">
											<div className="flex items-center justify-between">
												<Label
													htmlFor="description"
													className="text-sm font-medium text-base-content flex items-center gap-1"
												>
													{t("customerPlans.management.form.description")}
													<span className="text-error">*</span>
												</Label>
												<span
													className={`text-xs ${formData.description.length > MAX_DESCRIPTION_LENGTH ? "text-error" : "text-base-content/50"}`}
												>
													{t("customerPlans.management.form.nameMaxLength", {
														current: formData.description.length,
														max: MAX_DESCRIPTION_LENGTH,
													})}
												</span>
											</div>
											<Textarea
												id="description"
												value={formData.description}
												onChange={(e) => {
													const value = e.target.value.slice(
														0,
														MAX_DESCRIPTION_LENGTH,
													);
													setFormData({
														...formData,
														description: value,
													});
													if (validationErrors.description) {
														setValidationErrors({
															...validationErrors,
															description: undefined,
														});
													}
												}}
												placeholder={t(
													"customerPlans.management.form.descriptionPlaceholder",
												)}
												className={`min-h-[100px] resize-none transition-all ${
													validationErrors.description
														? "border-error focus:ring-error/20"
														: formData.description.trim()
															? "border-success/50 focus:border-success focus:ring-success/20"
															: ""
												}`}
											/>
											{validationErrors.description ? (
												<p className="text-sm text-error flex items-center gap-1">
													<AlertCircle className="h-3 w-3" />
													{validationErrors.description}
												</p>
											) : (
												<p className="text-xs text-base-content/60">
													{t("customerPlans.management.form.descriptionHint")}
												</p>
											)}
										</div>
									</div>
								</div>
								{/* Pricing & Billing Section */}
								<div className="space-y-4">
									<div className="flex items-center justify-between pb-2 border-b border-base-200">
										<div className="flex items-center gap-2">
											<div className="w-2 h-2 bg-primary rounded-full" />
											<h3 className="text-lg font-medium text-base-content">
												{t("customerPlans.management.form.sections.pricing")}
											</h3>
										</div>
										{formData.price &&
											Number.parseFloat(formData.price) > 0 && (
												<CheckCircle2 className="h-4 w-4 text-success" />
											)}
									</div>
									<div className="grid gap-4">
										<div className="grid grid-cols-2 gap-4">
											<div className="space-y-2">
												<Label
													htmlFor="price"
													className="text-sm font-medium text-base-content flex items-center gap-1"
												>
													{t("customerPlans.management.form.price")}
													<span className="text-error">*</span>
												</Label>
												<div className="relative">
													<span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 font-medium">
														{getOrganizationCurrencySymbol(
															currentOrganization.defaultCurrency,
														)}
													</span>
													<Input
														id="price"
														type="text"
														value={formData.price}
														onChange={(e) => {
															setFormData({
																...formData,
																price: e.target.value,
															});
															if (validationErrors.price) {
																setValidationErrors({
																	...validationErrors,
																	price: undefined,
																});
															}
														}}
														placeholder={t(
															"customerPlans.management.form.pricePlaceholder",
														)}
														className={`pl-10 h-10 transition-all ${
															validationErrors.price
																? "border-error focus:ring-error/20"
																: formData.price &&
																		Number.parseFloat(formData.price) > 0
																	? "border-success/50 focus:border-success focus:ring-success/20"
																	: ""
														}`}
													/>
												</div>
												{formData.price &&
													Number.parseFloat(formData.price) > 0 &&
													formData.billingCycle !== BillingCycle.MONTHLY && (
														<div className="text-xs text-info bg-info/10 px-2 py-1 rounded">
															{(() => {
																const monthlyAverage = calculateMonthlyAverage(
																	formData.price,
																	formData.billingCycle,
																);
																const formattedMonthlyAverage = formatCurrency(
																	monthlyAverage,
																	formData.currency,
																);
																return t(
																	"enrollments.planHelper.totalForPeriod",
																	{
																		monthlyAmount: formattedMonthlyAverage,
																	},
																);
															})()}
														</div>
													)}
												{validationErrors.price ? (
													<p className="text-sm text-error flex items-center gap-1">
														<AlertCircle className="h-3 w-3" />
														{validationErrors.price}
													</p>
												) : (
													<p className="text-xs text-base-content/60">
														{t("customerPlans.management.form.priceHint")}
													</p>
												)}
											</div>
											<div className="space-y-2">
												<Label
													htmlFor="currency"
													className="text-sm font-medium text-base-content flex items-center gap-1"
												>
													{t("customerPlans.management.form.currency")}
													<span className="text-error">*</span>
												</Label>
												<Select
													value={formData.currency}
													onValueChange={(value) =>
														setFormData({
															...formData,
															currency: value as Currency,
														})
													}
												>
													<SelectTrigger className="h-10">
														<SelectValue
															placeholder={t(
																"customerPlans.management.form.currencyPlaceholder",
															)}
														/>
													</SelectTrigger>
													<SelectContent>
														<SelectItem value={Currency.BRL}>
															{CURRENCY_LABELS[Currency.BRL]}
														</SelectItem>
														<SelectItem value={Currency.USD}>
															{CURRENCY_LABELS[Currency.USD]}
														</SelectItem>
													</SelectContent>
												</Select>
											</div>
										</div>
										<div className="space-y-2">
											<div className="flex items-center gap-2">
												<Label
													htmlFor="billingCycle"
													className="text-sm font-medium text-base-content flex items-center gap-1"
												>
													{t("customerPlans.management.form.billingCycle")}
													<span className="text-error">*</span>
												</Label>
												<Tooltip>
													<TooltipTrigger asChild>
														<HelpCircle className="h-3.5 w-3.5 text-base-content/40 cursor-help" />
													</TooltipTrigger>
													<TooltipContent>
														<p className="text-xs max-w-[200px]">
															{t(
																"customerPlans.management.form.billingCycleHint",
															)}
														</p>
													</TooltipContent>
												</Tooltip>
											</div>
											<Select
												value={formData.billingCycle}
												onValueChange={(value: BillingCycle) =>
													setFormData({ ...formData, billingCycle: value })
												}
											>
												<SelectTrigger className="h-10">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="MONTHLY">
														{t(
															"customerPlans.management.form.billingCycleOptions.MONTHLY",
														)}
													</SelectItem>
													<SelectItem value="QUARTERLY">
														{t(
															"customerPlans.management.form.billingCycleOptions.QUARTERLY",
														)}
													</SelectItem>
													<SelectItem value="SEMI_ANNUAL">
														{t(
															"customerPlans.management.form.billingCycleOptions.SEMI_ANNUAL",
														)}
													</SelectItem>
													<SelectItem value="ANNUALLY">
														{t(
															"customerPlans.management.form.billingCycleOptions.ANNUALLY",
														)}
													</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<div className="space-y-2">
											<div className="flex items-center gap-2">
												<Label
													htmlFor="weeklySessionsLimit"
													className="text-sm font-medium text-base-content"
												>
													{t(
														"customerPlans.management.form.weeklySessionsLimit",
													)}
												</Label>
												<span className="text-xs text-base-content/50 bg-base-200 px-1.5 py-0.5 rounded">
													{t(
														"customerPlans.management.form.establishmentsOptional",
													)}
												</span>
											</div>
											<Input
												id="weeklySessionsLimit"
												type="number"
												min="1"
												max="20"
												value={formData.weeklySessionsLimit}
												onChange={(e) => {
													setFormData({
														...formData,
														weeklySessionsLimit: e.target.value,
													});
													if (validationErrors.weeklySessionsLimit) {
														setValidationErrors({
															...validationErrors,
															weeklySessionsLimit: undefined,
														});
													}
												}}
												placeholder={t(
													"customerPlans.management.form.weeklySessionsLimitPlaceholder",
												)}
												className={`h-10 transition-all ${validationErrors.weeklySessionsLimit ? "border-error focus:ring-error/20" : ""}`}
											/>
											{validationErrors.weeklySessionsLimit ? (
												<p className="text-sm text-error flex items-center gap-1">
													<AlertCircle className="h-3 w-3" />
													{validationErrors.weeklySessionsLimit}
												</p>
											) : (
												<p className="text-xs text-base-content/60">
													{t(
														"customerPlans.management.form.weeklySessionsLimitHint",
													)}
												</p>
											)}
										</div>
									</div>
								</div>
								{/* Features Section */}
								<div className="space-y-4">
									<div className="flex items-center justify-between pb-2 border-b border-base-200">
										<div className="flex items-center gap-2">
											<div className="w-2 h-2 bg-primary rounded-full" />
											<h3 className="text-lg font-medium text-base-content">
												{t("customerPlans.management.form.sections.features")}
											</h3>
										</div>
										{formData.features.some((f) => f.value.trim()) && (
											<CheckCircle2 className="h-4 w-4 text-success" />
										)}
									</div>
									<div className="space-y-3">
										<div className="flex items-center justify-between">
											<Label className="text-sm font-medium text-base-content flex items-center gap-1">
												{t("customerPlans.management.form.features")}
												<span className="text-error">*</span>
											</Label>
											<span className="text-xs text-base-content/50">
												{formData.features.filter((f) => f.value.trim()).length}{" "}
												{formData.features.filter((f) => f.value.trim())
													.length === 1
													? "service"
													: "services"}
											</span>
										</div>
										<div className="space-y-2 bg-base-200/30 rounded-lg p-3">
											{formData.features.map((feature, index) => (
												<div
													key={feature.id}
													className="flex gap-2 items-center group"
												>
													<div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex-shrink-0">
														<Star className="h-3 w-3" />
													</div>
													<div className="flex-1">
														<Input
															value={feature.value}
															onChange={(e) =>
																updateFeature(index, e.target.value)
															}
															placeholder={t(
																"customerPlans.management.form.featuresPlaceholder",
															)}
															className={`h-9 bg-base-100 transition-all ${
																feature.value.trim() ? "border-success/30" : ""
															}`}
														/>
													</div>
													{formData.features.length > 1 && (
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	type="button"
																	variant="ghost"
																	size="sm"
																	onClick={() => removeFeature(index)}
																	className="h-9 w-9 p-0 flex-shrink-0 opacity-50 group-hover:opacity-100 hover:bg-error/10 hover:text-error transition-all"
																>
																	<X className="h-4 w-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent>
																<p className="text-xs">
																	{t(
																		"customerPlans.management.form.removeFeature",
																	)}
																</p>
															</TooltipContent>
														</Tooltip>
													)}
												</div>
											))}
										</div>
										<div className="flex flex-wrap items-center gap-2 mt-2">
											<span className="text-xs text-base-content/60">
												{t("customerPlans.management.form.quickServicesTitle")}
											</span>
											<div className="flex flex-wrap gap-2">
												{quickServiceOptions.map((option) => {
													const isSelected = formData.features.some(
														(feature) =>
															feature.value.trim().toLowerCase() ===
															option.label.trim().toLowerCase(),
													);
													return (
														<Button
															key={option.key}
															type="button"
															variant="outline"
															size="sm"
															onClick={() => addFeatureWithValue(option.label)}
															disabled={isSelected}
															className={`h-7 rounded-full border-dashed px-3 text-xs ${
																isSelected
																	? "bg-primary/10 border-primary/40 text-primary"
																	: "bg-base-100 hover:bg-primary/5 hover:border-primary/60 hover:text-primary"
															}`}
														>
															{option.label}
														</Button>
													);
												})}
											</div>
										</div>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={addFeature}
											className="w-fit h-9 text-sm hover:bg-primary/5 hover:border-primary hover:text-primary transition-all"
										>
											<Plus className="h-4 w-4 mr-2" />
											{t("customerPlans.management.form.addFeature")}
										</Button>
										{validationErrors.features ? (
											<p className="text-sm text-error flex items-center gap-1">
												<AlertCircle className="h-3 w-3" />
												{validationErrors.features}
											</p>
										) : (
											<p className="text-xs text-base-content/60">
												{t("customerPlans.management.form.featuresHint")}
											</p>
										)}
									</div>
								</div>
								{/* Establishments Section */}
								<div className="space-y-4">
									<div className="flex items-center gap-2 pb-2 border-b border-base-200">
										<div className="w-2 h-2 bg-primary rounded-full" />
										<h3 className="text-lg font-medium text-base-content">
											{t(
												"customerPlans.management.form.sections.establishments",
											)}
										</h3>
										<span className="text-xs text-base-content/50 bg-base-200 px-1.5 py-0.5 rounded">
											{t(
												"customerPlans.management.form.establishmentsOptional",
											)}
										</span>
									</div>
									<div className="space-y-2">
										<Label className="text-sm font-medium text-base-content">
											{t("customerPlans.management.form.establishments")}
										</Label>
										<Select
											value={
												formData.establishmentIds.length > 0 ? "selected" : ""
											}
											onValueChange={() => {}}
										>
											<SelectTrigger className="h-10">
												<SelectValue
													placeholder={t(
														"customerPlans.management.form.establishmentsPlaceholder",
													)}
												>
													{formData.establishmentIds.length > 0
														? `${formData.establishmentIds.length} ${t("customerPlans.management.form.establishmentsSelected")}`
														: t(
																"customerPlans.management.form.establishmentsPlaceholder",
															)}
												</SelectValue>
											</SelectTrigger>
											<SelectContent>
												<div className="p-2">
													<div className="space-y-2">
														{establishments.map((establishment) => (
															<div
																key={establishment.id}
																className="flex items-center space-x-2"
															>
																<input
																	type="checkbox"
																	id={`establishment-${establishment.id}`}
																	checked={formData.establishmentIds.includes(
																		establishment.id,
																	)}
																	onChange={(e) => {
																		if (e.target.checked) {
																			setFormData({
																				...formData,
																				establishmentIds: [
																					...formData.establishmentIds,
																					establishment.id,
																				],
																			});
																		} else {
																			setFormData({
																				...formData,
																				establishmentIds:
																					formData.establishmentIds.filter(
																						(id) => id !== establishment.id,
																					),
																			});
																		}
																	}}
																	className="checkbox checkbox-primary checkbox-sm"
																/>
																<Label
																	htmlFor={`establishment-${establishment.id}`}
																	className="text-sm cursor-pointer flex-1"
																>
																	{establishment.name}
																</Label>
															</div>
														))}
													</div>
													{formData.establishmentIds.length > 0 && (
														<div className="mt-3 pt-2 border-t border-base-200">
															<Button
																type="button"
																variant="outline"
																size="sm"
																onClick={() =>
																	setFormData({
																		...formData,
																		establishmentIds: [],
																	})
																}
																className="w-full h-8 text-xs"
															>
																{t(
																	"customerPlans.management.form.clearEstablishments",
																)}
															</Button>
														</div>
													)}
												</div>
											</SelectContent>
										</Select>
										<p className="text-xs text-base-content/60">
											{t("customerPlans.management.form.establishmentsHelp")}
										</p>
										{/* Selected Establishments List */}
										{formData.establishmentIds.length > 0 && (
											<div className="mt-4 p-3 bg-base-100 border border-base-200 rounded-lg">
												<div className="flex items-center gap-2 mb-2">
													<div className="w-1.5 h-1.5 bg-primary rounded-full" />
													<span className="text-sm font-medium text-base-content">
														{t(
															"customerPlans.management.form.selectedEstablishments",
														)}
													</span>
												</div>
												<div className="flex flex-wrap gap-2">
													{formData.establishmentIds.map((establishmentId) => {
														const establishment = establishments.find(
															(e) => e.id === establishmentId,
														);
														return establishment ? (
															<div
																key={establishmentId}
																className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-md px-2 py-1"
															>
																<span className="text-xs text-base-content">
																	{establishment.name}
																</span>
																<button
																	type="button"
																	onClick={() => {
																		setFormData({
																			...formData,
																			establishmentIds:
																				formData.establishmentIds.filter(
																					(id) => id !== establishmentId,
																				),
																		});
																	}}
																	className="text-base-content/60 hover:text-error transition-colors"
																	aria-label="Remove establishment"
																>
																	<svg
																		className="w-3 h-3"
																		fill="none"
																		stroke="currentColor"
																		viewBox="0 0 24 24"
																	>
																		<title>Remove establishment</title>
																		<path
																			strokeLinecap="round"
																			strokeLinejoin="round"
																			strokeWidth={2}
																			d="M6 18L18 6M6 6l12 12"
																		/>
																	</svg>
																</button>
															</div>
														) : null;
													})}
												</div>
											</div>
										)}
									</div>
								</div>
								{/* Status Section */}
								<div className="space-y-4">
									<div className="flex items-center gap-2 pb-2 border-b border-base-200">
										<div className="w-2 h-2 bg-primary rounded-full" />
										<h3 className="text-lg font-medium text-base-content">
											{t("customerPlans.management.form.sections.status")}
										</h3>
									</div>
									<div
										className={`flex items-center space-x-3 p-4 rounded-lg border transition-all ${
											formData.isActive
												? "bg-success/5 border-success/30"
												: "bg-base-100 border-base-200"
										}`}
									>
										<input
											type="checkbox"
											id="isActive"
											checked={formData.isActive}
											onChange={(e) =>
												setFormData({ ...formData, isActive: e.target.checked })
											}
											className="checkbox checkbox-primary"
										/>
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<Label
													htmlFor="isActive"
													className="text-sm font-medium text-base-content cursor-pointer"
												>
													{t("customerPlans.management.form.isActive")}
												</Label>
												{formData.isActive ? (
													<span className="text-xs bg-success/20 text-success px-2 py-0.5 rounded-full font-medium">
														Active
													</span>
												) : (
													<span className="text-xs bg-base-300 text-base-content/60 px-2 py-0.5 rounded-full">
														Inactive
													</span>
												)}
											</div>
											<p className="text-xs text-base-content/60 mt-1">
												{formData.isActive
													? t("customerPlans.management.form.statusDescription")
													: t("customerPlans.management.form.statusHint")}
											</p>
										</div>
									</div>
								</div>
							</div>
							<DialogFooter className="flex gap-3 pt-6 border-t border-base-300">
								<Button
									variant="outline"
									onClick={() => {
										setIsCreateDialogOpen(false);
										resetForm();
									}}
									disabled={isLoading}
									className="flex-1 h-11"
									data-testid="cancel-create-customer-plan-button"
								>
									{t("customerPlans.management.form.cancel")}
								</Button>
								<Button
									onClick={handleCreateCustomerPlan}
									disabled={isLoading || !formData.name.trim()}
									className="flex-1 h-11 bg-primary hover:bg-primary/90 text-primary-content"
									data-testid="submit-create-customer-plan-button"
								>
									{isLoading
										? t("customerPlans.management.form.creating")
										: t("customerPlans.management.form.create")}
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				)}
			</div>

			{/* Content */}
			<div
				className="h-full overflow-auto"
				data-testid="customer-plans-content"
			>
				{customerPlans.length === 0 ? (
					<Card data-testid="customer-plans-empty-state">
						<CardContent className="flex flex-col items-center justify-center py-12">
							<CreditCard className="h-12 w-12 text-base-content/30 mb-4" />
							<h3 className="text-lg font-medium text-base-content mb-2">
								{t("customerPlans.management.noPlans")}
							</h3>
							<p className="text-base-content/60 text-center mb-4">
								{t("customerPlans.management.noPlansDescription")}
							</p>
							{canManage && (
								<Button
									onClick={() => setIsCreateDialogOpen(true)}
									className="btn btn-primary text-primary-content"
									data-testid="create-first-customer-plan-button"
								>
									<Plus className="h-4 w-4 mr-2" />
									{t("customerPlans.management.createFirst")}
								</Button>
							)}
						</CardContent>
					</Card>
				) : (
					<CustomerPlanList
						customerPlans={customerPlans}
						establishments={establishments}
						organizationId={orgId}
						token={token}
						canManage={canManage}
						onEdit={handleEditCustomerPlan}
						isLoading={isLoading}
					/>
				)}
			</div>
			{/* Edit Dialog */}
			<Dialog
				open={!!editingPlan}
				onOpenChange={(open) => {
					if (!open) {
						setEditingPlan(null);
						resetForm();
					}
				}}
			>
				<DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
					<DialogHeader className="space-y-3 pb-6 border-b border-base-300">
						<DialogTitle className="text-xl font-semibold text-base-content flex items-center gap-2">
							<CreditCard className="h-5 w-5 text-primary" />
							{t("customerPlans.management.form.update")}
						</DialogTitle>
						<DialogDescription className="text-base-content/70">
							{t("customerPlans.management.description")}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-6 py-6">
						{/* Basic Information Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-2 pb-2 border-b border-base-200">
								<div className="w-2 h-2 bg-primary rounded-full" />
								<h3 className="text-lg font-medium text-base-content">
									{t("customerPlans.management.form.sections.basicInfo")}
								</h3>
							</div>
							<div className="grid gap-4">
								<div className="space-y-2">
									<Label
										htmlFor="edit-name"
										className="text-sm font-medium text-base-content"
									>
										{t("customerPlans.management.form.name")}
										<span className="text-error ml-1">*</span>
									</Label>
									<Input
										id="edit-name"
										value={formData.name}
										onChange={(e) =>
											setFormData({ ...formData, name: e.target.value })
										}
										placeholder={t(
											"customerPlans.management.form.namePlaceholder",
										)}
										className="h-10"
									/>
								</div>
								<div className="space-y-2">
									<Label
										htmlFor="edit-description"
										className="text-sm font-medium text-base-content"
									>
										{t("customerPlans.management.form.description")}
										<span className="text-error ml-1">*</span>
									</Label>
									<Textarea
										id="edit-description"
										value={formData.description}
										onChange={(e) =>
											setFormData({ ...formData, description: e.target.value })
										}
										placeholder={t(
											"customerPlans.management.form.descriptionPlaceholder",
										)}
										className="min-h-[80px] resize-none"
									/>
								</div>
							</div>
						</div>
						{/* Pricing & Billing Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-2 pb-2 border-b border-base-200">
								<div className="w-2 h-2 bg-primary rounded-full" />
								<h3 className="text-lg font-medium text-base-content">
									{t("customerPlans.management.form.sections.pricing")}
								</h3>
							</div>
							<div className="grid gap-4">
								<div className="grid grid-cols-2 gap-4">
									<div className="space-y-2">
										<Label
											htmlFor="edit-price"
											className="text-sm font-medium text-base-content"
										>
											{t("customerPlans.management.form.price")}
											<span className="text-error ml-1">*</span>
										</Label>
										<div className="relative">
											<span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 font-medium">
												{getOrganizationCurrencySymbol(formData.currency)}
											</span>
											<Input
												id="edit-price"
												type="number"
												step="0.01"
												min="0"
												value={formData.price}
												onChange={(e) =>
													setFormData({ ...formData, price: e.target.value })
												}
												placeholder={t(
													"customerPlans.management.form.pricePlaceholder",
												)}
												className="pl-10 h-10"
											/>
										</div>
										{formData.price &&
											Number.parseFloat(formData.price) > 0 &&
											formData.billingCycle !== BillingCycle.MONTHLY && (
												<div className="text-xs text-muted-foreground">
													{(() => {
														const monthlyAverage = calculateMonthlyAverage(
															formData.price,
															formData.billingCycle,
														);
														const formattedMonthlyAverage = formatCurrency(
															monthlyAverage,
															formData.currency,
														);
														return t("enrollments.planHelper.totalForPeriod", {
															monthlyAmount: formattedMonthlyAverage,
														});
													})()}
												</div>
											)}
									</div>
									<div className="space-y-2">
										<Label
											htmlFor="edit-currency"
											className="text-sm font-medium text-base-content"
										>
											{t("customerPlans.management.form.currency")}
											<span className="text-error ml-1">*</span>
										</Label>
										<Select
											value={formData.currency}
											onValueChange={(value) =>
												setFormData({
													...formData,
													currency: value as Currency,
												})
											}
										>
											<SelectTrigger className="h-10">
												<SelectValue
													placeholder={t(
														"customerPlans.management.form.currencyPlaceholder",
													)}
												/>
											</SelectTrigger>
											<SelectContent>
												<SelectItem value={Currency.BRL}>
													{CURRENCY_LABELS[Currency.BRL]}
												</SelectItem>
												<SelectItem value={Currency.USD}>
													{CURRENCY_LABELS[Currency.USD]}
												</SelectItem>
											</SelectContent>
										</Select>
									</div>
								</div>
								<div className="space-y-2">
									<Label
										htmlFor="edit-billingCycle"
										className="text-sm font-medium text-base-content"
									>
										{t("customerPlans.management.form.billingCycle")}
										<span className="text-error ml-1">*</span>
									</Label>
									<Select
										value={formData.billingCycle}
										onValueChange={(value: BillingCycle) =>
											setFormData({ ...formData, billingCycle: value })
										}
									>
										<SelectTrigger className="h-10">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="MONTHLY">
												{t(
													"customerPlans.management.form.billingCycleOptions.MONTHLY",
												)}
											</SelectItem>
											<SelectItem value="QUARTERLY">
												{t(
													"customerPlans.management.form.billingCycleOptions.QUARTERLY",
												)}
											</SelectItem>
											<SelectItem value="SEMI_ANNUAL">
												{t(
													"customerPlans.management.form.billingCycleOptions.SEMI_ANNUAL",
												)}
											</SelectItem>
											<SelectItem value="ANNUALLY">
												{t(
													"customerPlans.management.form.billingCycleOptions.ANNUALLY",
												)}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label
										htmlFor="edit-weeklySessionsLimit"
										className="text-sm font-medium text-base-content"
									>
										{t("customerPlans.management.form.weeklySessionsLimit")}
									</Label>
									<Input
										id="edit-weeklySessionsLimit"
										type="number"
										min="1"
										max="20"
										value={formData.weeklySessionsLimit}
										onChange={(e) => {
											setFormData({
												...formData,
												weeklySessionsLimit: e.target.value,
											});
											if (validationErrors.weeklySessionsLimit) {
												setValidationErrors({
													...validationErrors,
													weeklySessionsLimit: undefined,
												});
											}
										}}
										placeholder={t(
											"customerPlans.management.form.weeklySessionsLimitPlaceholder",
										)}
										className={`h-10 ${validationErrors.weeklySessionsLimit ? "border-error" : ""}`}
									/>
									{validationErrors.weeklySessionsLimit && (
										<p className="text-sm text-error">
											{validationErrors.weeklySessionsLimit}
										</p>
									)}
								</div>
							</div>
						</div>
						{/* Features Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-2 pb-2 border-b border-base-200">
								<div className="w-2 h-2 bg-primary rounded-full" />
								<h3 className="text-lg font-medium text-base-content">
									{t("customerPlans.management.form.sections.features")}
								</h3>
							</div>
							<div className="space-y-3">
								<Label className="text-sm font-medium text-base-content">
									{t("customerPlans.management.form.features")}
									<span className="text-error ml-1">*</span>
								</Label>
								<div className="flex flex-wrap items-center gap-2 mt-1">
									<span className="text-xs text-base-content/60">
										{t("customerPlans.management.form.quickServicesTitle")}
									</span>
									<div className="flex flex-wrap gap-2">
										{quickServiceOptions.map((option) => {
											const isSelected = formData.features.some(
												(feature) =>
													feature.value.trim().toLowerCase() ===
													option.label.trim().toLowerCase(),
											);
											return (
												<Button
													key={`edit-${option.key}`}
													type="button"
													variant="outline"
													size="sm"
													onClick={() => addFeatureWithValue(option.label)}
													disabled={isSelected}
													className={`h-7 rounded-full border-dashed px-3 text-xs ${
														isSelected
															? "bg-primary/10 border-primary/40 text-primary"
															: "bg-base-100 hover:bg-primary/5 hover:border-primary/60 hover:text-primary"
													}`}
												>
													{option.label}
												</Button>
											);
										})}
									</div>
								</div>
								<div className="space-y-3">
									{formData.features.map((feature, index) => (
										<div key={feature.id} className="flex gap-3 items-center">
											<div className="flex-1">
												<Input
													value={feature.value}
													onChange={(e) => updateFeature(index, e.target.value)}
													placeholder={t(
														"customerPlans.management.form.featuresPlaceholder",
													)}
													className="h-10"
												/>
											</div>
											{formData.features.length > 1 && (
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => removeFeature(index)}
													className="h-10 w-10 p-0 flex-shrink-0 hover:bg-error/10 hover:border-error hover:text-error"
												>
													<X className="h-4 w-4" />
												</Button>
											)}
										</div>
									))}
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={addFeature}
									className="w-fit h-9 text-sm"
								>
									<Plus className="h-4 w-4 mr-2" />
									{t("customerPlans.management.form.addFeature")}
								</Button>
							</div>
						</div>
						{/* Establishments Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-2 pb-2 border-b border-base-200">
								<div className="w-2 h-2 bg-primary rounded-full" />
								<h3 className="text-lg font-medium text-base-content">
									{t("customerPlans.management.form.sections.establishments")}
								</h3>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium text-base-content">
									{t("customerPlans.management.form.establishments")}
									<span className="text-base-content/60 ml-1 text-xs">
										({t("customerPlans.management.form.establishmentsOptional")}
										)
									</span>
								</Label>
								<Select
									value={formData.establishmentIds.length > 0 ? "selected" : ""}
									onValueChange={() => {}}
								>
									<SelectTrigger className="h-10">
										<SelectValue
											placeholder={t(
												"customerPlans.management.form.establishmentsPlaceholder",
											)}
										>
											{formData.establishmentIds.length > 0
												? `${formData.establishmentIds.length} ${t("customerPlans.management.form.establishmentsSelected")}`
												: t(
														"customerPlans.management.form.establishmentsPlaceholder",
													)}
										</SelectValue>
									</SelectTrigger>
									<SelectContent>
										<div className="p-2">
											<div className="space-y-2">
												{establishments.map((establishment) => (
													<div
														key={establishment.id}
														className="flex items-center space-x-2"
													>
														<input
															type="checkbox"
															id={`edit-establishment-${establishment.id}`}
															checked={formData.establishmentIds.includes(
																establishment.id,
															)}
															onChange={(e) => {
																if (e.target.checked) {
																	setFormData({
																		...formData,
																		establishmentIds: [
																			...formData.establishmentIds,
																			establishment.id,
																		],
																	});
																} else {
																	setFormData({
																		...formData,
																		establishmentIds:
																			formData.establishmentIds.filter(
																				(id) => id !== establishment.id,
																			),
																	});
																}
															}}
															className="checkbox checkbox-primary checkbox-sm"
														/>
														<Label
															htmlFor={`edit-establishment-${establishment.id}`}
															className="text-sm cursor-pointer flex-1"
														>
															{establishment.name}
														</Label>
													</div>
												))}
											</div>
											{formData.establishmentIds.length > 0 && (
												<div className="mt-3 pt-2 border-t border-base-200">
													<Button
														type="button"
														variant="outline"
														size="sm"
														onClick={() =>
															setFormData({ ...formData, establishmentIds: [] })
														}
														className="w-full h-8 text-xs"
													>
														{t(
															"customerPlans.management.form.clearEstablishments",
														)}
													</Button>
												</div>
											)}
										</div>
									</SelectContent>
								</Select>
								<p className="text-xs text-base-content/60">
									{t("customerPlans.management.form.establishmentsHelp")}
								</p>
								{/* Selected Establishments List */}
								{formData.establishmentIds.length > 0 && (
									<div className="mt-4 p-3 bg-base-100 border border-base-200 rounded-lg">
										<div className="flex items-center gap-2 mb-2">
											<div className="w-1.5 h-1.5 bg-primary rounded-full" />
											<span className="text-sm font-medium text-base-content">
												{t(
													"customerPlans.management.form.selectedEstablishments",
												)}
											</span>
										</div>
										<div className="flex flex-wrap gap-2">
											{formData.establishmentIds.map((establishmentId) => {
												const establishment = establishments.find(
													(e) => e.id === establishmentId,
												);
												return establishment ? (
													<div
														key={establishmentId}
														className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-md px-2 py-1"
													>
														<span className="text-xs text-base-content">
															{establishment.name}
														</span>
														<button
															type="button"
															onClick={() => {
																setFormData({
																	...formData,
																	establishmentIds:
																		formData.establishmentIds.filter(
																			(id) => id !== establishmentId,
																		),
																});
															}}
															className="text-base-content/60 hover:text-error transition-colors"
															aria-label="Remove establishment"
														>
															<svg
																className="w-3 h-3"
																fill="none"
																stroke="currentColor"
																viewBox="0 0 24 24"
															>
																<title>Remove establishment</title>
																<path
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth={2}
																	d="M6 18L18 6M6 6l12 12"
																/>
															</svg>
														</button>
													</div>
												) : null;
											})}
										</div>
									</div>
								)}
							</div>
						</div>
						{/* Status Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-2 pb-2 border-b border-base-200">
								<div className="w-2 h-2 bg-primary rounded-full" />
								<h3 className="text-lg font-medium text-base-content">
									{t("customerPlans.management.form.sections.status")}
								</h3>
							</div>
							<div className="flex items-center space-x-3 p-4 bg-base-100 rounded-lg border border-base-200">
								<input
									type="checkbox"
									id="edit-isActive"
									checked={formData.isActive}
									onChange={(e) =>
										setFormData({ ...formData, isActive: e.target.checked })
									}
									className="checkbox checkbox-primary"
								/>
								<div className="flex-1">
									<Label
										htmlFor="edit-isActive"
										className="text-sm font-medium text-base-content cursor-pointer"
									>
										{t("customerPlans.management.form.isActive")}
									</Label>
									<p className="text-xs text-base-content/60 mt-1">
										{t("customerPlans.management.form.statusDescription")}
									</p>
								</div>
							</div>
						</div>
					</div>
					<DialogFooter className="flex gap-3 pt-6 border-t border-base-300">
						<Button
							variant="outline"
							onClick={() => {
								setEditingPlan(null);
								resetForm();
							}}
							disabled={isLoading}
							className="flex-1 h-11"
						>
							{t("customerPlans.management.form.cancel")}
						</Button>
						<Button
							onClick={handleUpdateCustomerPlan}
							disabled={isLoading || !formData.name.trim()}
							className="flex-1 h-11 bg-primary hover:bg-primary/90 text-primary-content"
						>
							{isLoading
								? t("customerPlans.management.form.updating")
								: t("customerPlans.management.form.update")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
